import { tracked } from "@glimmer/tracking"
import { legacyCardFromGoogleClassroomNode } from "@shared/lib/card-stack/unified-migration"
import type {
  CardAttachment,
  GoogleClassroomClassSetting,
  ShareModeSettings,
} from "@shared/models/card-stack"
import {
  OPERATION_CONTRACTS,
  type OperationContract,
} from "@shared/unified-tiptap/analytics/operations"
import {
  asAttachmentId,
  asCardId,
  asGoogleClassId,
  type AttachmentId,
  type AttachmentSource,
  type NonEmptyTuple,
} from "@shared/unified-tiptap/analytics/types"
import { objectId } from "@shared/util/object-id"
import type { ExtensionCardGoogleClassroomAttrs } from "@tiptap-content/extensions/card-google-classroom/card-google-classroom-content-extension"
import type { Editor } from "@tiptap/core"
import type { Node as PMNode } from "@tiptap/pm/model"
import type {
  GoogleClassroomCardEditor,
  GoogleClassroomTextProp,
  NewAttachment,
} from "cc-frontend/lib/google-classroom/card-editor"

// The chain's `label()` command is declared by this module's `@tiptap/core` augmentation, which
// only reaches this program if the module is imported here.
import "@shared/unified-tiptap/extensions/operation-label/operation-label-extension"

/**
 * GoogleClassroomCardEditor for unified card stacks: every edit updates the
 * cardGoogleClassroom node in the lesson's Tiptap editor (the source of truth), and the
 * derived legacy `cards`/persistence flow through the editor's normal update pipeline
 * (`onEdited` — see UnifiedCardStackEditor's flushEditsRef).
 *
 * Call `dispose()` when the sidebar closes (it detaches the editor listener that keeps
 * `card` reads live).
 */
export class UnifiedGoogleClassroomCardEditor implements GoogleClassroomCardEditor {
  /** Bumped on every doc change so tracked `card` reads recompute (Glimmer tracking). */
  @tracked private docRevision = 0
  private readonly onTransaction: () => void

  constructor(
    private readonly deps: {
      editor: Editor
      cardId: string
      /** Called after each edit — flush the editor's update pipeline (derive `cards`, persist). */
      onEdited?: () => void
    },
  ) {
    this.onTransaction = () => this.docRevision++
    deps.editor.on("transaction", this.onTransaction)
  }

  dispose() {
    this.deps.editor.off("transaction", this.onTransaction)
  }

  private findNode(): { node: PMNode; pos: number } | undefined {
    let found: { node: PMNode; pos: number } | undefined
    this.deps.editor.state.doc.forEach((node, pos) => {
      if (!found && node.type.name === "cardGoogleClassroom" && node.attrs.id === this.deps.cardId)
        found = { node, pos }
    })
    return found
  }

  get card() {
    // Ember-tracked read: any editor transaction invalidates this getter,
    // which is watched by the Ember google-classroom modal.
    void this.docRevision
    const found = this.findNode()
    // It is important here that we don't return a supergrain object, since Ember will add its own auto-tracking.
    return found && legacyCardFromGoogleClassroomNode(found.node)
  }

  /**
   * Applies `changes` to the node's attrs in one transaction. `bumpRevision` mirrors
   * which legacy actions `.inc("attributes._revision")` — it marks the Google Classroom
   * post as needing an update. `operation`/`args` label the transaction, so the edit reports
   * that sidebar operation to analytics.
   */
  private updateNode<Args>(
    changes: (
      attrs: ExtensionCardGoogleClassroomAttrs,
    ) => Partial<ExtensionCardGoogleClassroomAttrs>,
    options: {
      isIntermediateEdit?: boolean
      operation: OperationContract<Args>
      args: NoInfer<Args>
    },
  ): void
  private updateNode(
    changes: (
      attrs: ExtensionCardGoogleClassroomAttrs,
    ) => Partial<ExtensionCardGoogleClassroomAttrs>,
    options?: { isIntermediateEdit?: boolean },
  ): void
  private updateNode(
    changes: (
      attrs: ExtensionCardGoogleClassroomAttrs,
    ) => Partial<ExtensionCardGoogleClassroomAttrs>,
    options: {
      isIntermediateEdit?: boolean
      operation?: OperationContract<unknown>
      args?: unknown
    } = {},
  ) {
    // Callers can run after an async confirm dialog, by which point the editor may be destroyed.
    if (this.deps.editor.isDestroyed) return

    const found = this.findNode()
    if (!found) return
    const attrs = found.node.attrs as ExtensionCardGoogleClassroomAttrs
    const changed = changes(attrs)

    const chain = this.deps.editor.chain()
    if (options.operation) {
      chain.label(options.operation, "google-classroom-sidebar", options.args)
    }

    chain
      .command(({ tr }) => {
        for (const [key, value] of Object.entries(changed)) {
          tr.setNodeAttribute(found.pos, key, value)
        }

        if (options.isIntermediateEdit) {
          // Intermediate edit: suppress the LESSON_UNIFIED_UPDATE action triggered in UnifiedCardStackEditor,
          // and don't bump the card revision.
          tr.setMeta("noUnifiedCardStackAction", true)
        } else {
          tr.setNodeAttribute(found.pos, "revision", attrs.revision + 1)
        }

        tr.setMeta("addToHistory", false)
        return true
      })
      .run()
    this.deps.onEdited?.()
  }

  private get analyticsCardId() {
    return asCardId(this.deps.cardId)
  }

  /** Replaces `classSetting`'s entry in classSettings (matched by googleClassId). */
  private replaceClassSetting(
    attrs: ExtensionCardGoogleClassroomAttrs,
    newSetting: GoogleClassroomClassSetting,
  ): Array<GoogleClassroomClassSetting> {
    return [
      ...attrs.classSettings.filter(
        (setting) => setting.googleClassId !== newSetting.googleClassId,
      ),
      newSetting,
    ]
  }

  editProp(prop: GoogleClassroomTextProp, value: string | null) {
    this.updateNode(() => ({ [prop]: value }), { isIntermediateEdit: true })
  }

  finishEditingProp(
    prop: GoogleClassroomTextProp,
    value: string | null,
    _originalValue: string | null,
    _wordCountChange: number,
  ) {
    // The commit owns the fact even though editProp already reflected `value` in the node:
    // while-typing edits are intermediate and report nothing, and this is the bump.
    this.updateNode(() => ({ [prop]: value }), {
      operation: OPERATION_CONTRACTS.OP_EDIT_GOOGLE_CLASSROOM_FIELD,
      args: { cardId: this.analyticsCardId, field: prop },
    })
  }

  setTopic(topic: string | null) {
    this.updateNode(
      (attrs) => ({ extraPostProperties: { ...attrs.extraPostProperties, topicName: topic } }),
      {
        operation: OPERATION_CONTRACTS.OP_SET_GOOGLE_CLASSROOM_TOPIC,
        args: { cardId: this.analyticsCardId, topic },
      },
    )
  }

  setChoices(choices: Array<string>) {
    this.updateNode(
      (attrs) => ({ extraPostProperties: { ...attrs.extraPostProperties, choices } }),
      {
        operation: OPERATION_CONTRACTS.OP_SET_GOOGLE_CLASSROOM_CHOICES,
        args: { cardId: this.analyticsCardId, choices },
      },
    )
  }

  addAttachments(files: Array<NewAttachment>, source: string) {
    const withIds: Array<CardAttachment> = files.map((file) => ({
      ...file,
      id: objectId(),
    }))
    const [first, ...rest] = withIds
    if (first === undefined) return
    const attachmentIds: NonEmptyTuple<AttachmentId> = [
      asAttachmentId(first.id),
      ...rest.map((attachment) => asAttachmentId(attachment.id)),
    ]
    this.updateNode((attrs) => ({ attachments: [...attrs.attachments, ...withIds] }), {
      operation: OPERATION_CONTRACTS.OP_INSERT_GOOGLE_CLASSROOM_ATTACHMENT,
      args: {
        cardId: this.analyticsCardId,
        attachmentIds,
        source: coerceAttachmentSource(source),
      },
    })
  }

  upsertShareMode(attachmentId: string, shareMode: ShareModeSettings) {
    this.updateNode(
      (attrs) => ({
        attachmentSettings: { ...attrs.attachmentSettings, [attachmentId]: { shareMode } },
      }),
      {
        operation: OPERATION_CONTRACTS.OP_SET_GOOGLE_CLASSROOM_SHARE_MODE,
        args: {
          cardId: this.analyticsCardId,
          attachmentId: asAttachmentId(attachmentId),
          shareMode,
        },
      },
    )
  }

  removeAttachment(attachment: CardAttachment) {
    this.updateNode(
      (attrs) => ({ attachments: attrs.attachments.filter((a) => a.id !== attachment.id) }),
      {
        operation: OPERATION_CONTRACTS.OP_REMOVE_GOOGLE_CLASSROOM_ATTACHMENT,
        args: { cardId: this.analyticsCardId, attachmentId: asAttachmentId(attachment.id) },
      },
    )
  }

  scheduleClass(classSetting: GoogleClassroomClassSetting, _className: string) {
    this.updateNode(
      (attrs) => ({
        classSettings: this.replaceClassSetting(attrs, { ...classSetting, isEnabled: true }),
        enabledGoogleClassIds: [
          ...attrs.enabledGoogleClassIds.filter((id) => id !== classSetting.googleClassId),
          classSetting.googleClassId,
        ],
      }),
      {
        operation: OPERATION_CONTRACTS.OP_SCHEDULE_GOOGLE_CLASSROOM_CLASS,
        args: {
          cardId: this.analyticsCardId,
          googleClassId: asGoogleClassId(classSetting.googleClassId),
          scheduled: true,
        },
      },
    )
  }

  unscheduleClass(classSetting: GoogleClassroomClassSetting, _className: string) {
    this.updateNode(
      (attrs) => ({
        classSettings: this.replaceClassSetting(attrs, { ...classSetting, isEnabled: false }),
        enabledGoogleClassIds: attrs.enabledGoogleClassIds.filter(
          (id) => id !== classSetting.googleClassId,
        ),
      }),
      {
        operation: OPERATION_CONTRACTS.OP_SCHEDULE_GOOGLE_CLASSROOM_CLASS,
        args: {
          cardId: this.analyticsCardId,
          googleClassId: asGoogleClassId(classSetting.googleClassId),
          scheduled: false,
        },
      },
    )
  }

  updateClassSetting(
    classSetting: GoogleClassroomClassSetting,
    prop: "scheduledAtTime" | "dueAt",
    value: GoogleClassroomClassSetting["scheduledAtTime"] | GoogleClassroomClassSetting["dueAt"],
  ) {
    this.updateNode(
      (attrs) => ({
        classSettings: this.replaceClassSetting(attrs, { ...classSetting, [prop]: value }),
      }),
      {
        operation: OPERATION_CONTRACTS.OP_SET_GOOGLE_CLASSROOM_CLASS_TIME,
        args: {
          cardId: this.analyticsCardId,
          googleClassId: asGoogleClassId(classSetting.googleClassId),
          field: prop,
        },
      },
    )
  }
}

/**
 * The `AttachmentSource` domain values, as a runtime allowlist for `coerceAttachmentSource`.
 */
const ATTACHMENT_SOURCES = [
  "upload",
  "google",
  "dropbox",
  "onedrive",
  "other",
  "link",
] as const satisfies readonly AttachmentSource[]

/**
 * Coerce a picker source string to a known `AttachmentSource`, folding anything outside the
 * domain to `other`. The one production caller is the Google Classroom modal
 * (planner/google-classroom/main.ts), which passes through whatever `AttachFilesDropdown`
 * emits — including `"drag"` (a local drag-and-drop upload), which is deliberately not an
 * `AttachmentSource` member.
 */
function coerceAttachmentSource(source: string): (typeof ATTACHMENT_SOURCES)[number] {
  return ATTACHMENT_SOURCES.find((known) => known === source) ?? "other"
}
